import { markReloadAttempt, shouldAttemptReload } from './staleChunkRecovery'

export interface RegisterServiceWorkerOptions {
  /** Path to the generated worker. Defaults to `/sw.js`. */
  url?: string
  /** How long to wait before asking the server for a new worker again. */
  updateIntervalMs?: number
}

/** Far more often than we deploy, and it costs one conditional GET. */
const DEFAULT_UPDATE_INTERVAL_MS = 60 * 60_000

const LEGACY_SUPABASE_CACHE = 'supabase-cache'

/**
 * Register the service worker, and keep the install from going stale.
 *
 * `registerType: 'autoUpdate'` (skipWaiting + clientsClaim) activates a new
 * worker as soon as one is found — but *finding* it used to happen only inside a
 * `load` listener. That is enough for a browser tab, which re-runs `load`
 * constantly. It is not enough for an installed PWA:
 *
 *  - iOS suspends and resumes a standalone PWA **without re-running `load`**, so
 *    a member who never cold-starts it never checks for a new worker.
 *  - The usual backstop, the browser's own update check on a navigation request,
 *    never fires either, because client-side routing issues no navigation.
 *
 * Between them an install can serve its precached bundle indefinitely, which is
 * how a member ended up having to delete and re-add the app to get a fix. So the
 * registration is kept, and re-checked whenever the app returns to the foreground.
 */
export function registerServiceWorker(options: RegisterServiceWorkerOptions = {}): void {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return
  // Older builds cached database responses here, shared by everyone on the device.
  void window.caches?.delete(LEGACY_SUPABASE_CACHE).catch(() => {})
  const { url = '/sw.js', updateIntervalMs = DEFAULT_UPDATE_INTERVAL_MS } = options

  // Reload when a new worker takes over, so a running tab stops executing the
  // code it was precached with — sharing the stale-chunk cooldown, because both
  // recoveries are one reload and neither should be able to loop.
  //
  // `hadController` is why this is not simply "reload on controllerchange":
  // clientsClaim() also fires it for a brand-new registration, claiming the very
  // page that registered it. That page is already running the newest code, so
  // reloading costs a visible flash, buys nothing, and burns the cooldown that
  // stale-chunk recovery may need seconds later.
  const hadController = Boolean(navigator.serviceWorker.controller)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || !shouldAttemptReload()) return
    markReloadAttempt()
    window.location.reload()
  })

  const start = () => {
    void navigator.serviceWorker
      // `updateViaCache: 'none'` keeps the HTTP cache out of the update check.
      // The `_headers` rule serving sw.js as no-cache does the same job from the
      // other side; belt and braces, because an install pinned to an old worker
      // is exactly the failure this function exists to prevent.
      .register(url, { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        // Registering just checked, so the clock starts now rather than firing a
        // second request the first time the app is foregrounded.
        let lastCheckedAt = Date.now()
        const checkForUpdate = () => {
          if (document.visibilityState !== 'visible') return
          const now = Date.now()
          if (now - lastCheckedAt < updateIntervalMs) return
          lastCheckedAt = now
          void registration.update().catch(() => {
            // Offline, or the server is unreachable. The next foreground retries.
          })
        }
        document.addEventListener('visibilitychange', checkForUpdate)
        window.addEventListener('focus', checkForUpdate)
      })
      .catch(() => {
        // Registration fails transiently (network, bots, iOS quirks) — not fatal.
      })
  }

  if (document.readyState === 'complete') start()
  else window.addEventListener('load', start, { once: true })
}
